import React, { FunctionComponent, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import { isString } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { INTEGRATED_BENEFITS_SECTION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Image } from '../../atoms/image/Image'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title } from '../../atoms/title/Title'
import { FeaturedBenefitsItemCardContent } from '../../hooks/useSpecialCaseBenefits'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { CarouselEdgeSlider } from '../carouselEdgeSlider/CarouselEdgeSlider'
import { IntegratedBenefitsItemProps } from '../integratedBenefitsItem/IntegratedBenefitsItem'

export interface FeaturedBenefitsSectionProps {
  benefits: IntegratedBenefitsItemProps[]
}

const TitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['24px'],
}))

const ImageContainer = styled(Image)(() => ({
  height: '148px',
}))

const CardItemTitleContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
}))

const CardItemTextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  padding: theme.spacing['16px'],
  flexGrow: 1,
}))

const CardPressableContainer = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  boxShadow: `0 2px 5px ${theme.colors.shadowLow}`,
  borderRadius: '16px',
  flexGrow: 1,
}))

const CardPressableInnerContainer = styled(View)<{ theme: ThemeType; isSlidingEdgeCarousel: boolean }>(
  ({ theme, isSlidingEdgeCarousel }) => ({
    borderRadius: '16px',
    overflow: 'hidden',
    flexGrow: 1,
    width: isSlidingEdgeCarousel
      ? 'auto'
      : theme.isLyraSelect
      ? theme.breakpoints.isMinWidthDesktop
        ? '290px'
        : '320px'
      : theme.breakpoints.isMinWidthDesktop
      ? '336px'
      : '310px',
  }),
)

const TitleText = styled(BodyText)({
  fontWeight: 500,
})

export const FeaturedBenefitsSection: FunctionComponent<FeaturedBenefitsSectionProps> = ({ benefits }) => {
  const { formatMessage } = useIntl()
  const { colors, breakpoints } = useTheme() as ThemeType
  const isSlidingEdgeCarousel = !breakpoints.isMinWidthMobileM

  const renderCard = useCallback(
    ({ item, index }: FeaturedBenefitsItemCardContent) => {
      const { featuredItemContent } = item
      const title = featuredItemContent?.title || item.title
      const description = featuredItemContent?.description || item.description
      const descriptionToDisplay = isString(description) ? description : formatMessage(description, { br: '\n' })
      const titleToDisplay = isString(title) ? title : formatMessage(title)
      const featuredImage = featuredItemContent?.image
      return (
        <CardPressableContainer
          key={index}
          testID={'FeaturedBenefitsItem-' + index}
          onPress={() => {
            item.onPress(INTEGRATED_BENEFITS_SECTION_TYPE.FEATURED)
          }}
        >
          <CardPressableInnerContainer isSlidingEdgeCarousel={isSlidingEdgeCarousel}>
            {featuredImage && (
              <ImageContainer source={{ uri: featuredImage }} contentFit='cover' accessibilityIgnoresInvertColors />
            )}
            <CardItemTextContainer>
              <CardItemTitleContainer>
                <TitleText
                  size={breakpoints.isMobileSized ? BodyTextSize.DEFAULT : BodyTextSize.LARGE}
                  text={titleToDisplay}
                  bold
                />
              </CardItemTitleContainer>
              <BodyText size={BodyTextSize.SMALL} text={descriptionToDisplay} color={colors.textSecondary} />
            </CardItemTextContainer>
          </CardPressableInnerContainer>
        </CardPressableContainer>
      )
    },
    [breakpoints.isMobileSized, colors.textSecondary, formatMessage, isSlidingEdgeCarousel],
  )

  return (
    <View testID={tID('FeaturedBenefitsSection')}>
      <TitleContainer>
        <Title
          level='3'
          size={breakpoints.isMobileSized ? TitleSize.SMALL : TitleSize.MEDIUM}
          text={<FormattedMessage defaultMessage='Featured benefits' description='Title for featured benefits' />}
        />
      </TitleContainer>
      <CarouselEdgeSlider
        items={benefits}
        renderItemCallback={renderCard}
        showEdge={isSlidingEdgeCarousel}
        contentContainerStyle={{
          columnGap: 15,
        }}
        paddingRight={breakpoints.isMobileSized ? 16 : 0}
      />
    </View>
  )
}
